import { useEffect, useMemo } from "react";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { Box, useMediaQuery, useTheme } from "@mui/material";

import {
  PARTNER_SPOTLIGHT_ENTRIES,
  type PartnerSpotlightEntry,
  partnerSpotlightLogoSrc,
} from "config/partnerSpotlightLogos";

const SLIDE_GAP_PX = 16;
const SLIDE_ROW_MIN_HEIGHT_PX = 88;
const LOGO_IMG_MAX_HEIGHT_PX = 52;

/** Slower advance; avoid permanent stop on scroll/focus (felt like random stops). */
const PARTNER_AUTOPLAY_DELAY_MS = 5200;

type PartnerSpotlightCarouselProps = {
  entries?: PartnerSpotlightEntry[];
};

export default function PartnerSpotlightCarousel({
  entries = PARTNER_SPOTLIGHT_ENTRIES,
}: PartnerSpotlightCarouselProps) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const isLg = useMediaQuery(theme.breakpoints.down("lg"));

  const slidesInView = useMemo(() => {
    if (isXs) return 2;
    if (isMd) return 3;
    if (isLg) return 4;
    return 6;
  }, [isXs, isMd, isLg]);

  const slideBasisCss = useMemo(
    () =>
      `calc((100% - ${(slidesInView - 1) * SLIDE_GAP_PX}px) / ${slidesInView})`,
    [slidesInView],
  );

  const autoplayPlugin = useMemo(
    () =>
      Autoplay({
        delay: PARTNER_AUTOPLAY_DELAY_MS,
        /** Page scroll / stray pointer must not kill the loop for the session */
        stopOnInteraction: false,
        stopOnMouseEnter: false,
        stopOnFocusIn: false,
        playOnInit: true,
      }),
    [],
  );

  const carouselAlign = useMemo(() => ({ align: "start" as const }), []);

  const canLoop = entries.length > slidesInView;

  const [viewportRef, emblaApi] = useEmblaCarousel(
    { ...carouselAlign, loop: canLoop },
    entries.length ? [autoplayPlugin] : [],
  );

  useEffect(() => {
    if (!entries.length || !emblaApi) return;
    const loopEnabled = entries.length > slidesInView;
    emblaApi.reInit({
      ...carouselAlign,
      loop: loopEnabled,
    });
    queueMicrotask(() => {
      autoplayPlugin.reset();
    });
  }, [
    emblaApi,
    carouselAlign,
    entries.length,
    slidesInView,
    autoplayPlugin,
  ]);

  if (entries.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 4,
        position: "relative",
        width: "100%",
        minWidth: 0,
        alignSelf: "stretch",
      }}
    >
      <Box
        sx={{ overflow: "hidden", width: "100%", px: { xs: 0, md: 0.25 } }}
        ref={viewportRef}
        role="region"
        aria-roledescription="carousel"
        aria-label="Partner logos"
      >
        <Box
          sx={{
            display: "flex",
            gap: `${SLIDE_GAP_PX}px`,
            touchAction: "pan-y pinch-zoom",
          }}
        >
          {entries.map((entry) => (
            <Box
              aria-roledescription="slide"
              key={entry.id}
              sx={{
                flex: `0 0 ${slideBasisCss}`,
                minWidth: 0,
                flexShrink: 0,
                boxSizing: "border-box",
                minHeight: SLIDE_ROW_MIN_HEIGHT_PX,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 0.75,
              }}
            >
              <Box
                component="img"
                src={partnerSpotlightLogoSrc(entry.file)}
                alt={entry.alt}
                loading="eager"
                sx={{
                  maxWidth: "100%",
                  maxHeight: `${LOGO_IMG_MAX_HEIGHT_PX}px`,
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
